import { useState } from 'react'
import { useMutation } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, UserPlus } from 'lucide-react'
import { adminInviteCccTrial } from '@/gen/cortado/v1/admin-AdminService_connectquery'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export interface CccTrialInviteForm {
  email: string
  firstName: string
  lastName: string
  companyName: string
}

const EMPTY_FORM: CccTrialInviteForm = { email: '', firstName: '', lastName: '', companyName: '' }

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Trims the form into an AdminInviteCccTrial request, or returns null while a
// field is missing or the email is malformed. The backend re-validates.
export function buildCccTrialInviteRequest(form: CccTrialInviteForm): CccTrialInviteForm | null {
  const request = {
    email: form.email.trim(),
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    companyName: form.companyName.trim(),
  }
  if (!request.firstName || !request.lastName || !request.companyName) return null
  if (!EMAIL_PATTERN.test(request.email)) return null
  return request
}

// Provisions a Coffee Cart Collective member's account with a 90-day trial
// and emails them a sign-in link (AdminInviteCccTrial).
export function CccTrialInviteButton() {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<CccTrialInviteForm>(EMPTY_FORM)
  const queryClient = useQueryClient()

  const mutation = useMutation(adminInviteCccTrial, {
    onSuccess: (response, request) => {
      toast.success(`Invite sent to ${request.email}`, {
        description: `${request.companyName} starts with a ${response.trialDays}-day trial.`,
      })
      setForm(EMPTY_FORM)
      setOpen(false)
      void queryClient.invalidateQueries()
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to send invite')
    },
  })

  const request = buildCccTrialInviteRequest(form)
  const update = (field: keyof CccTrialInviteForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }))

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <UserPlus className="h-4 w-4" />
        Invite CCC member
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite a Coffee Cart Collective member</DialogTitle>
            <DialogDescription>
              Creates their account and organization on CCC pricing with a 90-day trial, then emails
              them a sign-in link. The trial starts when they add a payment method.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (request) mutation.mutate(request)
            }}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="ccc-invite-first-name">First name</Label>
                <Input
                  id="ccc-invite-first-name"
                  value={form.firstName}
                  onChange={update('firstName')}
                  autoComplete="off"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ccc-invite-last-name">Last name</Label>
                <Input
                  id="ccc-invite-last-name"
                  value={form.lastName}
                  onChange={update('lastName')}
                  autoComplete="off"
                  required
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ccc-invite-email">Email</Label>
              <Input
                id="ccc-invite-email"
                type="email"
                value={form.email}
                onChange={update('email')}
                autoComplete="off"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ccc-invite-company">Company name</Label>
              <Input
                id="ccc-invite-company"
                value={form.companyName}
                onChange={update('companyName')}
                autoComplete="off"
                required
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!request || mutation.isPending}>
                {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Send invite
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
